// Core
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import {
  Button,
  Chip,
  ConfirmDialog,
  DataTable,
  FieldSearchSelect,
  FieldText,
  SectionCard,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useGetFixedBilateralPricesQuery,
  useUpsertFixedBilateralPricesMutation,
  useUpdateFixedBilateralPriceMutation,
  useDeleteFixedBilateralPriceMutation,
} from "state/bilateralPrices/bilateralPricesApiSlice";
import { openNotificationPopup } from "state/application/applicationSlice";

// Utils
import { useGetMembers } from "utils/crud/read";

const EMPTY_FORM = { providerId: "", receiverId: "", price: "" };

function ManageFixedBilateralPrices() {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["community"]);
  const members = useGetMembers("true") ?? [];

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingPair, setEditingPair] = useState(null); // { receiver_user_id, provider_user_id }
  const [pendingDelete, setPendingDelete] = useState(null);

  const { data: existingPairs = [], isFetching } = useGetFixedBilateralPricesQuery(
    { communityId: community.id },
    { refetchOnMountOrArgChange: true }
  );

  const [upsert, { isLoading: isUpserting }] = useUpsertFixedBilateralPricesMutation();
  const [update, { isLoading: isUpdating }] = useUpdateFixedBilateralPriceMutation();
  const [deletePair, { isLoading: isDeleting }] = useDeleteFixedBilateralPriceMutation();

  const isBusy = isUpserting || isUpdating || isDeleting;

  const memberOptions = [
    { value: "", label: t("fixedBilateralPrices.form.selectPlaceholder") },
    ...members.map((m) => ({ value: m.id, label: m.name })),
  ];
  const getMemberName = (id) => members.find((m) => m.id === id)?.name ?? id;

  const notify = (key, type) => dispatch(openNotificationPopup({ notification: t(key), type }));

  const isDuplicate = (providerId, receiverId) =>
    existingPairs.some((p) => {
      if (p.provider_user_id !== providerId || p.receiver_user_id !== receiverId) return false;
      if (
        editingPair &&
        editingPair.provider_user_id === providerId &&
        editingPair.receiver_user_id === receiverId
      ) {
        return false;
      }
      return true;
    });

  const handleSave = async () => {
    const { providerId, receiverId, price } = form;
    if (!providerId || !receiverId) {
      notify("fixedBilateralPrices.errors.selectBoth", "error");
      return;
    }
    if (providerId === receiverId) {
      notify("fixedBilateralPrices.errors.sameUser", "error");
      return;
    }
    const parsedPrice = parseFloat(price);
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      notify("fixedBilateralPrices.errors.invalidPrice", "error");
      return;
    }

    try {
      if (editingPair) {
        await update({
          communityId: community.id,
          receiverUserId: editingPair.receiver_user_id,
          providerUserId: editingPair.provider_user_id,
          price: parsedPrice,
        }).unwrap();
        notify("fixedBilateralPrices.success.updated", "success");
      } else {
        if (isDuplicate(providerId, receiverId)) {
          notify("fixedBilateralPrices.errors.duplicate", "error");
          return;
        }
        await upsert({
          communityId: community.id,
          prices: [
            { provider_user_id: providerId, receiver_user_id: receiverId, price: parsedPrice },
          ],
        }).unwrap();
        notify("fixedBilateralPrices.success.saved", "success");
      }
      setForm(EMPTY_FORM);
      setEditingPair(null);
    } catch {
      notify("fixedBilateralPrices.errors.generic", "error");
    }
  };

  const handleEdit = (pair) => {
    setForm({
      providerId: pair.provider_user_id,
      receiverId: pair.receiver_user_id,
      price: String(pair.price),
    });
    setEditingPair({
      provider_user_id: pair.provider_user_id,
      receiver_user_id: pair.receiver_user_id,
    });
  };

  const handleCancelEdit = () => {
    setForm(EMPTY_FORM);
    setEditingPair(null);
  };

  const confirmDelete = async () => {
    const pair = pendingDelete;
    setPendingDelete(null);
    if (!pair) return;
    try {
      await deletePair({
        communityId: community.id,
        receiverUserId: pair.receiver_user_id,
        providerUserId: pair.provider_user_id,
      }).unwrap();
      notify("fixedBilateralPrices.success.deleted", "success");
    } catch {
      notify("fixedBilateralPrices.errors.generic", "error");
    }
  };

  const rows = existingPairs.map((pair) => ({
    id: `${pair.provider_user_id}-${pair.receiver_user_id}`,
    provider: getMemberName(pair.provider_user_id),
    receiver: getMemberName(pair.receiver_user_id),
    price: pair.price,
    pair,
  }));

  const columns = [
    { key: "provider", label: t("fixedBilateralPrices.table.provider") },
    { key: "receiver", label: t("fixedBilateralPrices.table.receiver") },
    {
      key: "price",
      label: t("fixedBilateralPrices.table.price"),
      render: (v) => <Chip tone="teal">{`${v} €/kWh`}</Chip>,
    },
    {
      key: "actions",
      label: t("fixedBilateralPrices.table.actions"),
      align: "right",
      sort: false,
      render: (_v, row) => (
        <div className="inline-flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={isBusy}
            title={t("fixedBilateralPrices.table.edit")}
            onClick={() => handleEdit(row.pair)}
          >
            <Icon name="edit" style={{ fontSize: 18 }} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={isBusy}
            title={t("fixedBilateralPrices.table.delete")}
            onClick={() => setPendingDelete(row.pair)}
          >
            <Icon name="delete" style={{ fontSize: 18, color: "var(--danger)" }} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-rec">
      <SectionCard
        icon={editingPair ? "edit" : "add_circle"}
        title={
          editingPair
            ? t("fixedBilateralPrices.form.editTitle")
            : t("fixedBilateralPrices.form.addTitle")
        }
        sub={t("fixedBilateralPrices.subtitle")}
      >
        <div className="grid gap-4 items-end" style={{ gridTemplateColumns: "1fr 1fr 160px auto" }}>
          <FieldSearchSelect
            label={t("fixedBilateralPrices.form.provider")}
            value={form.providerId}
            onChange={(v) => setForm((f) => ({ ...f, providerId: v }))}
            options={memberOptions}
            disabled={!!editingPair}
          />
          <FieldSearchSelect
            label={t("fixedBilateralPrices.form.receiver")}
            value={form.receiverId}
            onChange={(v) => setForm((f) => ({ ...f, receiverId: v }))}
            options={memberOptions}
            disabled={!!editingPair}
          />
          <FieldText
            label={t("fixedBilateralPrices.form.price")}
            value={form.price}
            onChange={(v) => setForm((f) => ({ ...f, price: v }))}
            type="number"
            min={0}
            step="any"
          />
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={isBusy}>
              <Icon name="save" />
              {editingPair
                ? t("fixedBilateralPrices.form.save")
                : t("fixedBilateralPrices.form.add")}
            </Button>
            {editingPair && (
              <Button variant="ghost" onClick={handleCancelEdit} disabled={isBusy}>
                <Icon name="close" />
              </Button>
            )}
          </div>
        </div>
      </SectionCard>

      <div className="rec-card">
        <div className="rec-card-pad pb-0">
          <h3 className="rec-section-title">
            <Icon name="table_rows" />
            {t("fixedBilateralPrices.table.title")}
            {isFetching && <Icon name="hourglass_top" style={{ fontSize: 14 }} />}
          </h3>
        </div>
        <DataTable
          columns={columns}
          rows={rows}
          search={false}
          footer={false}
          emptyText={t("fixedBilateralPrices.table.empty")}
        />
      </div>

      <ConfirmDialog
        open={!!pendingDelete}
        danger
        title={t("fixedBilateralPrices.table.delete")}
        body={
          pendingDelete
            ? `${getMemberName(pendingDelete.provider_user_id)} → ${getMemberName(
                pendingDelete.receiver_user_id
              )}`
            : ""
        }
        confirmLabel={t("fixedBilateralPrices.table.delete")}
        cancelLabel={t("fixedBilateralPrices.form.cancel")}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}

export default ManageFixedBilateralPrices;
